import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { usePaginationPageSize } from './pagination-preferences';
import { HostingPleskSiteStatus } from '@tosho/domain';
import { HostingDeclaredSiteEditor } from './HostingDeclaredSiteEditor';
import { HostingDeclaredSiteRow } from './HostingDeclaredSiteRow';
import { hostingSitesApi, hostingSiteError } from './hosting-sites-api';
import type {
  HostingSiteCatalogPage,
  HostingDeclaredSiteView,
  HostingSiteDomainType,
  HostingSiteEnvironmentFilter,
} from '@tosho/contracts';
import { HostingSitePleskStatus, siteStatusObservation } from './HostingSitePleskStatus';
import { HostingSiteBackupSummary } from './HostingSiteBackups';
import { formatBackupBytes } from './hosting-storage-format';
import { useEffect, useId, useRef, useState } from 'react';
import type { HostingPleskDomainView, HostingServerView } from '@tosho/contracts';
import { clientDirectoryApi } from './client-directory-api';
import { BusinessLogo } from './BusinessLogo';
import { PrototypeIcon } from './PrototypeIcon';
import { HostingSiteProfileDialog } from './HostingSiteProfileDialog';
import {
  EMPTY_SITE_PROFILE,
  SITE_CMS,
  SITE_ENVIRONMENTS,
  siteCreationDate,
} from './hosting-site-profile';
import { HostingClientPicker, type HostingClientChoice } from './HostingClientPicker';
import { hostingServerApi } from './hosting-server-api';
import {
  hostingPleskInventoryApi,
  hostingPleskInventoryError,
  type HostingPleskDomainQuery,
} from './hosting-plesk-inventory-api';
export function HostingPleskDomainsPanel({
  csrfToken,
  onClose,
  initialServer = null,
  serverLocked = false,
  onReauthenticate,
  organizationLabel = 'Organisation',
  organizationLogoUrl = null,
  initialSite = null,
}: {
  readonly csrfToken: string;
  readonly initialSite?: HostingSiteCatalogPage['items'][number] | null;
  readonly onClose: () => void;
  readonly initialServer?: HostingServerView | null;
  readonly serverLocked?: boolean;
  readonly onReauthenticate?: (() => void) | undefined;
  readonly organizationLabel?: string;
  readonly organizationLogoUrl?: string | null;
}): React.JSX.Element {
  const [search, setSearch] = useState(initialSite?.asciiName ?? ''),
    [serverId, setServerId] = useState(initialServer?.id ?? ''),
    [owner, setOwner] = useState('ALL'),
    [client, setClient] = useState('ALL'),
    [presence, setPresence] = useState<HostingPleskDomainQuery['presence']>('CURRENT'),
    [domainType, setDomainType] = useState<HostingSiteDomainType>('ALL'),
    [environment, setEnvironment] = useState<HostingSiteEnvironmentFilter>('ALL'),
    [page, setPage] = useState(0),
    [size, setSize] = usePaginationPageSize(),
    [visit, setVisit] = useState(0);
  const [data, setData] = useState<HostingSiteCatalogPage | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState<string | null>(null),
    [feedback, setFeedback] = useState<string | null>(null),
    [editor, setEditor] = useState<HostingPleskDomainView | null>(null),
    [profileEditor, setProfileEditor] = useState<HostingPleskDomainView | null>(
      initialSite && initialSite.source !== 'DECLARED' ? initialSite : null,
    );
  const [declaredEditor, setDeclaredEditor] = useState<{
    site: HostingDeclaredSiteView | null;
  } | null>(initialSite?.source === 'DECLARED' ? { site: initialSite } : null);
  const pendingFocus = useRef<{ id: string; visit: number } | null>(null),
    completedVisit = useRef(-1);
  useEffect(() => {
    const pending = pendingFocus.current;
    if (pending && !loading && completedVisit.current === pending.visit) {
      (
        document.getElementById(pending.id) ?? document.getElementById('hosting-sites-refresh')
      )?.focus();
      pendingFocus.current = null;
    }
  }, [loading, data, visit]);
  const [servers, setServers] = useState<readonly HostingServerView[]>(
      initialServer ? [initialServer] : [],
    ),
    [serverPage, setServerPage] = useState(0),
    [serverTotal, setServerTotal] = useState(0),
    [serverLoading, setServerLoading] = useState(true),
    [serverError, setServerError] = useState(false),
    [serverVisit, setServerVisit] = useState(0);
  useEffect(() => {
    let active = true;
    setServerLoading(true);
    setServerError(false);
    void hostingServerApi
      .list(serverPage, 20)
      .then(
        (result) => {
          if (active) {
            setServers((old) =>
              serverPage === 0
                ? [
                    ...(initialServer && !result.items.some((s) => s.id === initialServer.id)
                      ? [initialServer]
                      : []),
                    ...result.items,
                  ]
                : [
                    ...old,
                    ...result.items.filter((row) => !old.some((prior) => prior.id === row.id)),
                  ],
            );
            setServerTotal(result.total);
            if (result.items.length > 0 && (serverPage + 1) * 20 < result.total)
              setServerPage(serverPage + 1);
          }
        },
        () => {
          if (active) setServerError(true);
        },
      )
      .finally(() => {
        if (active) setServerLoading(false);
      });
    return () => {
      active = false;
    };
  }, [serverPage, serverVisit, initialServer]);
  const quietVisit = useRef(false);
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!editor && !profileEditor && !declaredEditor && !document.hidden) {
        quietVisit.current = true;
        setVisit((n) => n + 1);
      }
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [editor, profileEditor, declaredEditor]);
  useEffect(() => {
    let active = true;
    const quiet = quietVisit.current;
    quietVisit.current = false;
    if (!quiet) {
      setLoading(true);
      setError(null);
    }
    const timer = window.setTimeout(() => {
      void hostingSitesApi
        .list({
          search,
          serverId,
          client,
          owner,
          presence,
          domainType,
          environment,
          page,
          pageSize: size,
        })
        .then(
          (result) => {
            if (active) setData(result);
          },
          (failure: unknown) => {
            if (active) {
              if (!quiet) {
                setData(null);
                setError(hostingSiteError(failure, 'READ'));
              }
            }
          },
        )
        .finally(() => {
          if (active) {
            completedVisit.current = visit;
            setLoading(false);
          }
        });
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [search, serverId, client, owner, presence, domainType, environment, page, size, visit]);
  const [clients, setClients] = useState<readonly HostingClientChoice[]>([]),
    [clientPage, setClientPage] = useState(0),
    [clientTotal, setClientTotal] = useState(0),
    [clientsLoading, setClientsLoading] = useState(true),
    [clientsError, setClientsError] = useState(false),
    [clientVisit, setClientVisit] = useState(0);
  useEffect(() => {
    let active = true;
    setClientsLoading(true);
    setClientsError(false);
    void clientDirectoryApi
      .search('', clientPage, 'clients')
      .then(
        (result) => {
          if (!active) return;
          const confirmed = result.clients.filter(
            (c) => c.stage === 'CLIENT' && c.origin !== 'FREEBE_LEGACY_UNREVIEWED',
          );
          setClients((old) =>
            clientPage === 0
              ? confirmed
              : [...old, ...confirmed.filter((c) => !old.some((x) => x.id === c.id))],
          );
          setClientTotal(result.clientCount);
          if (result.clients.length > 0 && (clientPage + 1) * 20 < result.clientCount)
            setClientPage(clientPage + 1);
        },
        () => {
          if (active) setClientsError(true);
        },
      )
      .finally(() => {
        if (active) setClientsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clientPage, clientVisit]);
  return (
    <div className="hosting-servers hosting-sites">
      <header className="dashboard-heading hosting-servers__heading">
        <div>
          <p className="dashboard-heading__eyebrow">Serveurs reliés</p>
          <h1>Sites observés</h1>
          <p>
            {initialServer ? initialServer.label + ' · ' : ''}Sites, Clients et suivi des serveurs
            administrés.
          </p>
        </div>
        <div className="hosting-sites__page-actions">
          <button
            type="button"
            id="hosting-sites-create"
            className="hosting-servers__primary"
            onClick={() => {
              setFeedback(null);
              setDeclaredEditor({ site: null });
            }}
          >
            <PrototypeIcon name="plus" />
            Référencer un site
          </button>
          <button type="button" className="hosting-sites__navigation" onClick={onClose}>
            <PrototypeIcon name="arrow-left" /> Retour aux serveurs
          </button>
        </div>
      </header>
      {feedback ? (
        <p role="status" className="hosting-servers__feedback tosho-feedback is-success">
          {feedback}
        </p>
      ) : null}
      <div className="hosting-sites__summary" aria-label="Compteurs des observations filtrées">
        {(
          [
            ['database', 'sites référencés', data?.summary?.domains],
            ['network', 'serveurs représentés', data?.summary?.servers],
            ['users-round', 'Clients rattachés', data?.summary?.clients],
            ['user-round-help', 'sites à rattacher', data?.summary?.unassigned],
          ] as const
        ).map(([icon, label, value]) => (
          <div
            className={
              'hosting-sites__metric' +
              (icon === 'database'
                ? ' is-dark'
                : icon === 'network'
                  ? ' is-cream'
                  : icon === 'user-round-help' && !loading && (value ?? 0) > 0
                    ? ' is-attention'
                    : '')
            }
            key={label}
          >
            <PrototypeIcon name={icon} />
            <div>
              <strong>
                {loading || !(data?.observedServers || data?.declaredServers)
                  ? '—'
                  : (value ?? '—')}
              </strong>
              <span>{label}</span>
            </div>
          </div>
        ))}
      </div>
      <section className="hosting-servers__card" aria-label="Domaines Plesk observés">
        <div className="hosting-servers__toolbar hosting-servers__catalogue-toolbar">
          <div className="hosting-servers__toolbar-title">
            <PrototypeIcon name="network" />
            <div>
              <h2>Sites et Clients</h2>
              <p>Domaines observés et rattachements</p>
            </div>
          </div>
          <button
            type="button"
            id="hosting-sites-refresh"
            className="hosting-sites__refresh"
            aria-label="Actualiser la vue"
            title="Actualiser la vue depuis Tosho"
            disabled={loading}
            onClick={() => setVisit((n) => n + 1)}
          >
            <PrototypeIcon name="refresh-cw" />
          </button>
        </div>
        <div className="hosting-sites__filters">
          <label className="hosting-sites__search-field">
            <PrototypeIcon name="search" />
            <span className="sr-only">Rechercher un site, un Client ou un serveur</span>
            <input
              placeholder="Rechercher un site, un Client ou un serveur…"
              value={search}
              maxLength={160}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
            />
          </label>
          <div className="hosting-sites__filter-choice">
            <label>
              <span className="sr-only">Serveur</span>
              <select
                value={serverId}
                disabled={serverLocked}
                onChange={(event) => {
                  setServerId(event.target.value);
                  setPage(0);
                }}
              >
                <option value="">Tous les serveurs</option>
                {servers
                  .filter((s) => s.administration === 'ORGANIZATION')
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
              </select>
              <PrototypeIcon name="chevron-down" className="hosting-sites__select-icon" />
            </label>
            {serverLoading ? (
              <p className="hosting-sites__help" role="status">
                Chargement des serveurs…
              </p>
            ) : serverError ? (
              <p className="hosting-sites__help tosho-feedback is-error" role="alert">
                La liste des serveurs n’a pas pu être chargée.{' '}
                <button type="button" onClick={() => setServerVisit((n) => n + 1)}>
                  Réessayer les serveurs
                </button>
              </p>
            ) : null}
          </div>
          <div className="hosting-sites__filter-choice">
            <label>
              <span className="sr-only">Rattachement Client</span>
              <select
                value={client}
                onChange={(event) => {
                  setClient(event.target.value);
                  setPage(0);
                }}
              >
                <option value="ALL">Tous les rattachements</option>
                <option value="UNASSIGNED">À rattacher</option>
                <option value="ORGANIZATION">Sites de {organizationLabel}</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </select>
              <PrototypeIcon name="chevron-down" className="hosting-sites__select-icon" />
            </label>
            {clientsError ? (
              <p role="alert" className="hosting-sites__help tosho-feedback is-error">
                Le filtre Clients n’a pas pu être chargé.{' '}
                <button type="button" onClick={() => setClientVisit((n) => n + 1)}>
                  Réessayer les Clients
                </button>
              </p>
            ) : null}
          </div>
          <label>
            <span className="sr-only">Propriétaire du serveur</span>
            <select
              value={owner}
              onChange={(event) => {
                setOwner(event.target.value);
                setPage(0);
              }}
            >
              <option value="ALL">Tous les propriétaires</option>
              <option value="ORGANIZATION">{organizationLabel}</option>
              <option value="CLIENT">Serveurs appartenant aux Clients</option>
              {Array.from(
                new Map(
                  servers
                    .filter((s) => s.ownerKind === 'CLIENT' && s.ownerClientId)
                    .map((s) => [s.ownerClientId!, s.ownerClientDisplayName]),
                ).entries(),
              ).map(([id, name]) => (
                <option key={id} value={id}>
                  {name ?? 'Client propriétaire'}
                </option>
              ))}
            </select>
            <PrototypeIcon name="chevron-down" className="hosting-sites__select-icon" />
          </label>
          <label>
            <span className="sr-only">Observations</span>
            <select
              value={presence}
              onChange={(event) => {
                setPresence(event.target.value as HostingPleskDomainQuery['presence']);
                setPage(0);
              }}
            >
              <option value="CURRENT">Derniers inventaires</option>
              <option value="MISSING">Non retrouvés au dernier inventaire</option>
              <option value="ALL">Toutes les observations conservées</option>
            </select>
            <PrototypeIcon name="chevron-down" className="hosting-sites__select-icon" />
          </label>
        </div>
        <div className="hosting-sites__filters hosting-sites__filters--secondary">
          <label>
            <span className="sr-only">Type de domaine</span>
            <select
              value={domainType}
              onChange={(event) => {
                setDomainType(event.target.value as HostingSiteDomainType);
                setPage(0);
              }}
            >
              <option value="ALL">Domaines et sous-domaines</option>
              <option value="DOMAIN">Domaines</option>
              <option value="SUBDOMAIN">Sous-domaines</option>
            </select>
            <PrototypeIcon name="chevron-down" className="hosting-sites__select-icon" />
          </label>
          <label>
            <span className="sr-only">Environnement du site</span>
            <select
              value={environment}
              onChange={(event) => {
                setEnvironment(event.target.value as HostingSiteEnvironmentFilter);
                setPage(0);
              }}
            >
              <option value="ALL">Tous les environnements</option>
              {Object.entries(SITE_ENVIRONMENTS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <PrototypeIcon name="chevron-down" className="hosting-sites__select-icon" />
          </label>
        </div>
        {loading ? (
          <p role="status" className="hosting-sites__help">
            Chargement des observations…
          </p>
        ) : error ? (
          <p role="alert" className="hosting-servers__error tosho-feedback is-error">
            {error}
          </p>
        ) : data && !data.observedServers && !data.declaredServers ? (
          <div className="hosting-sites__help">
            <h2>Aucun site encore référencé</h2>
            <p>
              Référencez un site hors Plesk ou ouvrez les réglages Plesk d’un serveur pour lire son
              inventaire.
            </p>
          </div>
        ) : data && !data.items.length ? (
          <p className="hosting-sites__help">
            Aucun site retourné pour ces filtres. Les derniers inventaires réussis sont conservés.
          </p>
        ) : data ? (
          <table className="hosting-servers__table">
            <caption className="sr-only">Domaines observés et Clients rattachés</caption>
            <thead>
              <tr>
                <th>Site et Client</th>
                <th>Serveur</th>
                <th>Dernière sauvegarde</th>
                <th>Stockage</th>
                <th>Vhost / domaine</th>
                <th>État et dernière lecture</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((domain) => {
                if (domain.source === 'DECLARED')
                  return (
                    <HostingDeclaredSiteRow
                      key={domain.id}
                      site={domain}
                      organizationLabel={organizationLabel}
                      organizationLogoUrl={organizationLogoUrl}
                      onEdit={() => setDeclaredEditor({ site: domain })}
                    />
                  );
                const profile = domain.profile ?? EMPTY_SITE_PROFILE,
                  cms = SITE_CMS[profile.cms];
                return (
                  <tr
                    key={domain.id}
                    className={
                      new HostingPleskSiteStatus(siteStatusObservation(domain)?.statusCode).kind ===
                      'INACTIVE'
                        ? 'is-inactive'
                        : undefined
                    }
                  >
                    <td data-label="Site et Client">
                      <strong
                        className={
                          domain.parentDomainName &&
                          domain.asciiName.endsWith('.' + domain.parentDomainName)
                            ? 'hosting-sites__subdomain'
                            : undefined
                        }
                      >
                        {domain.asciiName}
                      </strong>
                      {domain.parentDomainName &&
                        domain.asciiName.endsWith('.' + domain.parentDomainName) && (
                          <small className="hosting-sites__parent">
                            Sous-domaine de {domain.parentDomainName}
                          </small>
                        )}
                      <span
                        className={
                          'hosting-sites__environment is-' + profile.environment.toLowerCase()
                        }
                        title="Environnement déclaré dans Tosho"
                      >
                        {SITE_ENVIRONMENTS[profile.environment]}
                      </span>
                      <div className="hosting-sites__identity">
                        <div>
                          <small>{domain.organizationSite ? 'Organisation' : 'Client'}</small>
                          {domain.organizationSite ? (
                            <div className="hosting-sites__organization">
                              <BusinessLogo
                                name={organizationLabel}
                                logoUrl={organizationLogoUrl}
                              />
                            </div>
                          ) : domain.client ? (
                            <BusinessLogo
                              name={domain.client.displayName}
                              logoUrl={
                                domain.client.hasLogo
                                  ? `/api/clients/${encodeURIComponent(domain.client.id)}/logo?v=${domain.client.logoVersion}`
                                  : null
                              }
                            />
                          ) : (
                            <small>À rattacher</small>
                          )}
                        </div>
                        <div>
                          <small>CMS</small>
                          <div className="hosting-sites__cms" title="CMS déclaré dans Tosho">
                            {cms.logo ? (
                              <img src={cms.logo} alt={cms.name} />
                            ) : (
                              <span>{profile.cms === 'OTHER' ? profile.otherCms : cms.name}</span>
                            )}
                          </div>
                        </div>
                        <div className="hosting-sites__mobile-server">
                          <small>Serveur</small>
                          <span>
                            <PrototypeIcon name="network" />
                          </span>
                          <strong>{domain.serverLabel}</strong>
                        </div>
                        <p className="hosting-sites__mobile-owner">
                          Propriétaire :{' '}
                          {domain.serverOwnerKind === 'CLIENT'
                            ? (domain.serverOwnerClient?.displayName ?? 'Client propriétaire')
                            : organizationLabel}
                        </p>
                      </div>
                    </td>
                    <td data-label="Serveur">
                      <strong>{domain.serverLabel}</strong>
                      <small>
                        Propriétaire :{' '}
                        {domain.serverOwnerKind === 'CLIENT'
                          ? (domain.serverOwnerClient?.displayName ?? 'Client propriétaire')
                          : organizationLabel}
                      </small>
                    </td>
                    <td data-label="Dernière sauvegarde">
                      <HostingSiteBackupSummary value={domain.remoteBackup} />
                    </td>
                    <td data-label="Stockage" className="hosting-sites__storage">
                      <div className="hosting-backup-summary">
                        <span>
                          <PrototypeIcon name="database" /> <span>Sur le disque</span>
                        </span>
                        <strong>
                          {formatBackupBytes(domain.technicalSummary?.diskUsageBytes ?? null)}
                        </strong>
                        {domain.technicalSummary && !domain.technicalSummary.accessIsCurrent && (
                          <small>Relevé historique</small>
                        )}
                        {domain.backupSummary && domain.backupSummary.storage !== 'LOCAL' && (
                          <>
                            <small className="hosting-sites__desktop-remote-size">
                              Backup Google Drive :{' '}
                              {formatBackupBytes(domain.backupSummary.totalSizeBytes)}
                            </small>
                            <small className="hosting-sites__remote-size">
                              <span>Backup Google Drive</span>
                              <b>{formatBackupBytes(domain.backupSummary.totalSizeBytes)}</b>
                            </small>
                          </>
                        )}
                      </div>
                    </td>
                    <td data-label="Vhost / domaine">
                      {hostingLabels[domain.hostingType]}
                      <small>
                        {profile.createdOn
                          ? 'Créé le ' + siteCreationDate(profile.createdOn)
                          : domain.technicalSummary?.createdOn
                            ? 'Créé le ' + siteCreationDate(domain.technicalSummary.createdOn)
                            : 'Création à renseigner'}
                      </small>
                      {domain.parentRemoteId ? (
                        <small>
                          {domain.parentDomainName
                            ? 'Lié à ' + domain.parentDomainName
                            : 'Domaine parent inconnu'}
                        </small>
                      ) : null}
                    </td>
                    <td data-label="État et dernière lecture">
                      <HostingSitePleskStatus
                        value={siteStatusObservation(domain)}
                        inventoryObservedAt={domain.lastObservedAt}
                      />
                      {!domain.presentInLatest && <small>Non retrouvé au dernier inventaire</small>}
                      {!domain.accessIsCurrent ? (
                        <small>L’accès a changé depuis la lecture</small>
                      ) : null}
                    </td>
                    <td className="hosting-sites__mobile-vhost">
                      <details>
                        <summary>Vhost et dernière lecture</summary>
                        <div>
                          {' '}
                          {hostingLabels[domain.hostingType]}
                          <small>
                            {profile.createdOn
                              ? 'Créé le ' + siteCreationDate(profile.createdOn)
                              : domain.technicalSummary?.createdOn
                                ? 'Créé le ' + siteCreationDate(domain.technicalSummary.createdOn)
                                : 'Création à renseigner'}
                          </small>
                          {domain.parentRemoteId ? (
                            <small>
                              {domain.parentDomainName
                                ? 'Lié à ' + domain.parentDomainName
                                : 'Domaine parent inconnu'}
                            </small>
                          ) : null}
                        </div>
                      </details>
                      <HostingSitePleskStatus
                        value={siteStatusObservation(domain)}
                        inventoryObservedAt={domain.lastObservedAt}
                      />
                      {!domain.presentInLatest && <small>Non retrouvé au dernier inventaire</small>}
                      {!domain.accessIsCurrent && <small>L’accès a changé depuis la lecture</small>}
                    </td>
                    <td className="hosting-servers__actions" data-label="Actions">
                      <div className="hosting-sites__actions">
                        <button
                          type="button"
                          id={'hosting-site-profile-' + domain.id}
                          aria-label={'Modifier le vhost — ' + domain.asciiName}
                          title={'Modifier le vhost — ' + domain.asciiName}
                          className="hosting-servers__icon-button"
                          onClick={() => {
                            setFeedback(null);
                            setProfileEditor(domain);
                          }}
                        >
                          <PrototypeIcon name="pencil" />
                          <span className="hosting-sites__mobile-action">Modifier</span>
                        </button>
                        <button
                          type="button"
                          id={'hosting-site-client-' + domain.id}
                          aria-label={'Rattacher le site — ' + domain.asciiName}
                          title={'Rattacher le site — ' + domain.asciiName}
                          className="hosting-servers__icon-button"
                          disabled={
                            (!domain.presentInLatest || !domain.accessIsCurrent) &&
                            !domain.client &&
                            !domain.organizationSite
                          }
                          onClick={() => {
                            setFeedback(null);
                            setEditor(domain);
                          }}
                        >
                          <PrototypeIcon name="users-round" />
                          <span className="hosting-sites__mobile-action">Rattachement</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
        <footer className="hosting-servers__pagination">
          <label>
            Par page{' '}
            <select
              value={size}
              onChange={(event) => {
                setSize(Number(event.target.value));
                setPage(0);
              }}
            >
              {[5, 10, 20, 30, 50, 75, 100].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </label>
          <span>
            Page {page + 1}
            {data ? ' sur ' + Math.max(1, Math.ceil(data.total / size)) : ''}
          </span>
          <div>
            <button
              type="button"
              disabled={loading || page === 0}
              onClick={() => setPage((n) => n - 1)}
            >
              Précédente
            </button>
            <button
              type="button"
              disabled={loading || !data || (page + 1) * size >= data.total}
              onClick={() => setPage((n) => n + 1)}
            >
              Suivante
            </button>
          </div>
        </footer>
      </section>
      {declaredEditor ? (
        <HostingDeclaredSiteEditor
          site={declaredEditor.site}
          serverError={serverError}
          clientsError={clientsError}
          retryServers={() => setServerVisit((n) => n + 1)}
          retryClients={() => setClientVisit((n) => n + 1)}
          serverId={serverId}
          servers={servers}
          clients={clients}
          csrfToken={csrfToken}
          moreServers={servers.length < serverTotal && !serverLoading}
          loadServers={() => setServerPage((n) => n + 1)}
          moreClients={clients.length < clientTotal && !clientsLoading}
          loadClients={() => setClientPage((n) => n + 1)}
          onClose={() => {
            pendingFocus.current = {
              id: declaredEditor.site
                ? 'hosting-declared-site-' + declaredEditor.site.id
                : 'hosting-sites-create',
              visit: visit + 1,
            };
            setDeclaredEditor(null);
            setVisit((n) => n + 1);
          }}
          onSaved={() => {
            pendingFocus.current = {
              id: declaredEditor.site
                ? 'hosting-declared-site-' + declaredEditor.site.id
                : 'hosting-sites-create',
              visit: visit + 1,
            };
            setDeclaredEditor(null);
            setFeedback('Le site est référencé dans Tosho.');
            setVisit((n) => n + 1);
          }}
        />
      ) : null}
      {profileEditor ? (
        <HostingSiteProfileDialog
          domain={profileEditor}
          onReauthenticate={onReauthenticate}
          csrfToken={csrfToken}
          onClose={() => {
            pendingFocus.current = {
              id: 'hosting-site-profile-' + profileEditor.id,
              visit: visit + 1,
            };
            setProfileEditor(null);
            setVisit((n) => n + 1);
          }}
          onSaved={() => {
            pendingFocus.current = {
              id: 'hosting-site-profile-' + profileEditor.id,
              visit: visit + 1,
            };
            setFeedback('La fiche du vhost est enregistrée.');
            setProfileEditor(null);
            setVisit((n) => n + 1);
          }}
        />
      ) : null}
      {editor ? (
        <DomainAssignmentEditor
          domain={editor}
          organizationLabel={organizationLabel}
          csrfToken={csrfToken}
          onClose={() => {
            pendingFocus.current = { id: 'hosting-site-client-' + editor.id, visit: visit + 1 };
            setEditor(null);
            setVisit((n) => n + 1);
          }}
          onSaved={() => {
            pendingFocus.current = { id: 'hosting-site-client-' + editor.id, visit: visit + 1 };
            setFeedback('Le rattachement du site est enregistré.');
            setEditor(null);
            setVisit((n) => n + 1);
          }}
        />
      ) : null}
    </div>
  );
}
function DomainAssignmentEditor({
  domain,
  organizationLabel,
  csrfToken,
  onClose,
  onSaved,
}: {
  readonly domain: HostingPleskDomainView;
  readonly organizationLabel: string;
  readonly csrfToken: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null),
    title = useId();
  const [client, setClient] = useState<HostingClientChoice | null>(
      domain.client
        ? {
            id: domain.client.id,
            displayName: domain.client.displayName,
            hasLogo: domain.client.hasLogo,
            version: domain.client.logoVersion,
          }
        : null,
    ),
    [organizationSite, setOrganizationSite] = useState(domain.organizationSite ?? false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null),
    [locked, setLocked] = useState(false);
  const current = domain.presentInLatest && domain.accessIsCurrent;
  useEffect(() => {
    const previous = document.activeElement,
      element = dialog.current;
    element?.showModal();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  async function save(): Promise<void> {
    if (busy || locked || (!current && (client !== null || organizationSite))) return;
    setBusy(true);
    setError(null);
    try {
      await hostingPleskInventoryApi.assignClient(
        domain.id,
        domain.revision,
        client?.id ?? null,
        csrfToken,
        organizationSite,
      );
      onSaved();
    } catch (failure) {
      setError(hostingPleskInventoryError(failure));
      setLocked(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <ModalDialog
      ref={dialog}
      className="hosting-servers-editor"
      aria-labelledby={title}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      close={
        <ModalCloseButton
          className="tosho-modal-close"
          aria-label="Fermer"
          disabled={busy}
          onClick={onClose}
        />
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <header className="tosho-modal-header">
          <div>
            <p className="hosting-servers-editor__eyebrow">Sites observés</p>
            <h2 id={title}>Rattacher le site</h2>
          </div>
        </header>
        <p>
          <strong
            className={
              domain.parentDomainName && domain.asciiName.endsWith('.' + domain.parentDomainName)
                ? 'hosting-sites__subdomain'
                : undefined
            }
          >
            {domain.asciiName}
          </strong>
          {domain.parentDomainName && domain.asciiName.endsWith('.' + domain.parentDomainName) && (
            <small className="hosting-sites__parent">
              Sous-domaine de {domain.parentDomainName}
            </small>
          )}{' '}
          · {domain.serverLabel}
        </p>
        <p>
          Choisissez un Client ou un site de votre organisation. La propriété du serveur et les
          adresses déclarées du Client sont conservées.
        </p>
        {error ? (
          <p role="alert" className="hosting-servers__error tosho-feedback is-error">
            {error}
          </p>
        ) : null}
        <fieldset disabled={busy || locked}>
          {current ? (
            <>
              <div className="hosting-site-assignment__choices">
                <button
                  type="button"
                  aria-pressed={organizationSite}
                  onClick={() => {
                    setOrganizationSite(true);
                    setClient(null);
                  }}
                >
                  <PrototypeIcon name="briefcase-business" />
                  Site de {organizationLabel}
                </button>
                <button
                  type="button"
                  aria-pressed={!organizationSite}
                  onClick={() => setOrganizationSite(false)}
                >
                  <PrototypeIcon name="users-round" />
                  Client ou sans rattachement
                </button>
              </div>
              {organizationSite ? (
                <p>
                  Ce site appartient à {organizationLabel}. Aucun Client ni rôle supplémentaire
                  n’est créé.
                </p>
              ) : (
                <HostingClientPicker value={client} onChange={setClient} />
              )}
            </>
          ) : (
            <>
              <p>
                Une nouvelle lecture est nécessaire avant d’attribuer ce site à un autre Client.
              </p>
              <button
                type="button"
                onClick={() => {
                  setClient(null);
                  setOrganizationSite(false);
                }}
              >
                Détacher le rattachement
              </button>
            </>
          )}
        </fieldset>
        <footer>
          <button type="button" onClick={onClose} disabled={busy}>
            {locked ? 'Fermer et actualiser' : 'Annuler'}
          </button>
          <button
            type="submit"
            className="hosting-servers__primary"
            disabled={
              busy ||
              locked ||
              (!current && (client !== null || organizationSite)) ||
              ((client?.id ?? null) === (domain.client?.id ?? null) &&
                organizationSite === (domain.organizationSite ?? false))
            }
          >
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </footer>
      </form>
    </ModalDialog>
  );
}
const hostingLabels = {
  virtual: 'Hébergement web',
  standard_forwarding: 'Redirection standard',
  frame_forwarding: 'Redirection par cadre',
  none: 'Sans hébergement web',
} as const;
